import React from 'react';
import { canStartScheduledInspection as canStartScheduledInspectionFn } from '../../../lib/inspections';
import type { IconComponent } from '../../icons';
import { ArrowPathIcon, CheckIcon, HourglassIcon } from '../../icons';
import { Button } from '@safetyhub/ui';
import { REPORT_DISABLED_REASON_ID } from '../../../lib/reportGate';

interface ActionButton {
  id: string;
  label: string;
  /** 아이콘 컴포넌트(`components/icons`). 문자열 글리프는 받지 않는다. */
  icon?: IconComponent;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  disabled?: boolean;
  loading?: boolean;
}

interface BottomActionBarProps {
  /** 액션 버튼들 (최대 3개 권장) */
  actions: ActionButton[];
  /** 추가 정보 (왼쪽에 표시) */
  info?: React.ReactNode;
  /** 고정 여부 */
  fixed?: boolean;
}

export function BottomActionBar({
  actions,
  info,
  fixed = true,
}: BottomActionBarProps) {
  return (
    <div
      className={`
        bg-white border-t px-4 py-3 safe-area-inset-bottom
        ${fixed ? 'fixed bottom-0 left-0 right-0 z-40' : ''}
      `}
    >
      <div className="flex items-center gap-3">
        {/* 왼쪽 정보 영역 */}
        {info && <div className="flex-shrink-0">{info}</div>}

        {/* 액션 버튼들 */}
        <div className="flex-1 flex gap-2 justify-end">
          {actions.map((action) => (
            <Button
              key={action.id}
              variant={action.variant ?? 'secondary'}
              size="lg"
              onClick={action.onClick}
              disabled={action.disabled || action.loading}
              className="flex-1 max-w-[140px]"
            >
              {action.loading ? (
                <HourglassIcon className="h-4 w-4 animate-spin" />
              ) : action.icon ? (
                <action.icon className="h-4 w-4" />
              ) : null}
              <span className="truncate">{action.label}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 점검 상세 페이지용 하단 액션 바
 */
interface InspectionActionBarProps {
  /** 사진 추가 핸들러 */
  onAddPhoto: () => void;
  /** 카메라 촬영 모달 열기 */
  onOpenCamera?: () => void;
  /** 선택된 사진들 검토 완료 핸들러 */
  onSelectiveReview: () => void;
  /** 선택된 사진들 삭제 핸들러 */
  onSelectiveDelete: () => void;
  /** 선택된 사진들 재분석 핸들러 */
  onSelectiveReanalyze: () => void;
  /** 보고서 생성 핸들러 */
  onGenerateReport: () => void;
  /** 전체 선택/해제 핸들러 */
  onSelectAll: () => void;
  /** 검토 진행 상태 */
  reviewProgress: {
    reviewed: number;
    total: number;
    analyzing?: number;
    pendingReview?: number;
  };
  /** 선택된 사진 수 */
  selectedCount: number;
  /** 전체 사진 수 */
  totalCount: number;
  /** 전체 선택 여부 */
  isAllSelected: boolean;
  /** 보고서 생성 가능 여부 */
  canGenerateReport: boolean;
  /** 보고서 생성 비활성 사유 */
  reportDisabledReason?: string;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 재분석 로딩 상태 */
  isReanalyzing?: boolean;
  /** 재분석 진행 상황 */
  reanalysisProgress?: { current: number; total: number };
  /** 점검 완료 상태 */
  isCompleted?: boolean;
  /** 보고서 생성 여부 (생성된 경우 사진 추가 불가) */
  hasReport?: boolean;
  /** 편집 비활성화 (보고서 생성 + 점검완료 + 이후 차수 점검) */
  isEditDisabled?: boolean;
  /** AI 분석 활성화 여부 */
  enableAIAnalysis?: boolean;
  /** AI 분석 활성화 여부 변경 */
  setEnableAIAnalysis?: (enabled: boolean) => void;
  /** 발견사항 선택 기능 활성화 시 검토 관련 UI 숨김 */
  findingSelectionEnabled?: boolean;
  /** 보고서 생성 버튼 라벨 커스텀 */
  reportLabel?: string;
  /** 점검완료 서명 상태 */
  completion?: {
    enabled: boolean;
    canSign: boolean;
    canCancel: boolean;
    canRequest: boolean;
    isSubmitting: boolean;
    isMyCompleted: boolean;
    completedCount: number;
    totalCount: number;
    signDisabledReason: string;
    onSign: () => void;
    onManage: () => void;
  };
  /** SCHEDULED 상태 여부 */
  isScheduled?: boolean;
  /** 예약 점검 시작 핸들러 */
  onStartScheduled?: () => void;
  /** 예약일 (시작 가능 여부 판단용) */
  scheduledAt?: string;
}

export function InspectionActionBar({
  onOpenCamera,
  onSelectiveReview,
  onSelectiveDelete,
  onSelectiveReanalyze,
  onGenerateReport,
  onSelectAll,
  reviewProgress,
  selectedCount,
  totalCount,
  isAllSelected,
  canGenerateReport,
  reportDisabledReason,
  isLoading = false,
  isReanalyzing = false,
  reanalysisProgress,
  isCompleted = false,
  hasReport = false,
  isEditDisabled = false,
  findingSelectionEnabled = false,
  reportLabel,
  completion,
  isScheduled = false,
  onStartScheduled,
  scheduledAt,
}: InspectionActionBarProps) {
  // SCHEDULED 상태에서는 점검 시작 버튼만 표시
  if (isScheduled) {
    const canStart = !scheduledAt || canStartScheduledInspectionFn(scheduledAt);
    return (
      <div className="fixed left-0 right-0 z-40 bg-white border-t border-line" style={{ bottom: 'var(--bottom-nav-height, 0px)' }}>
        <div className="flex items-center justify-end py-3 px-4">
          {onStartScheduled && canStart ? (
            <Button tone="violet" onClick={onStartScheduled}>
              점검 시작
            </Button>
          ) : (
            <span className="text-sm text-ink-400">예약일 이후에 점검을 시작할 수 있습니다.</span>
          )}
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="fixed left-0 right-0 z-40 bg-white border-t border-line" style={{ bottom: 'var(--bottom-nav-height, 0px)' }}>
        <div className="flex items-center justify-center py-3 px-4">
          <span className="text-green-600 font-medium flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            점검 완료됨
          </span>
        </div>
      </div>
    );
  }

  const isAllReviewed = reviewProgress.reviewed >= reviewProgress.total && reviewProgress.total > 0;

  return (
    <div className="fixed bottom-16 left-0 right-0 z-40 bg-white border-t border-line">
      {/* 보고서 생성 알림 (점검완료 안내는 사진 영역 위에 별도 표시) */}
      {hasReport && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 text-sm border-b border-amber-200">
          <span>보고서가 생성되어 수정할 수 없습니다</span>
        </div>
      )}

      {/* 상단 행: 전체 선택, 선택 재분석, 선택 검토 완료 (가로 스크롤) */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-line-soft bg-surface-soft overflow-x-auto">
        {/* 전체 선택 버튼 */}
        <button
          type="button"
          onClick={onSelectAll}
          disabled={isLoading || hasReport || isEditDisabled || totalCount === 0}
          title={hasReport || isEditDisabled ? '수정할 수 없는 상태입니다' : totalCount === 0 ? '선택 가능한 사진이 없습니다' : undefined}
          className={`
            flex items-center gap-1 px-2 py-1.5 rounded-control text-xs font-medium whitespace-nowrap flex-shrink-0
            transition-colors disabled:opacity-50
            ${isAllSelected
              ? 'bg-brand-100 text-brand-700'
              : 'bg-white text-ink-600 border hover:bg-surface-hover'
            }
          `}
        >
          {isAllSelected ? (
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
          )}
          {isAllSelected ? '해제' : `전체(${totalCount})`}
        </button>

        {/* 선택 재분석 버튼 */}
        <button
          type="button"
          onClick={onSelectiveReanalyze}
          disabled={isLoading || hasReport || isEditDisabled || isReanalyzing || selectedCount === 0}
          title={hasReport ? '보고서가 생성되어 수정할 수 없습니다' : undefined}
          className={`
            flex items-center gap-1 px-2 py-1.5 rounded-control text-xs font-medium whitespace-nowrap flex-shrink-0
            transition-colors disabled:opacity-50
            ${isReanalyzing
              ? 'bg-yellow-100 text-yellow-700'
              : selectedCount > 0 && !hasReport
                ? 'bg-brand-50 text-brand-600 hover:bg-brand-100'
                : 'bg-white text-ink-400 border'
            }
          `}
        >
          {isReanalyzing ? (
            <>
              <HourglassIcon className="h-3.5 w-3.5 animate-spin" />
              {reanalysisProgress
                ? `${reanalysisProgress.current}/${reanalysisProgress.total}`
                : '...'}
            </>
          ) : (
            <>
              <ArrowPathIcon className="h-3.5 w-3.5" />
              {selectedCount}
            </>
          )}
        </button>

        {/* 선택 검토 완료 버튼 (findingSelectionEnabled 시 숨김) */}
        {!findingSelectionEnabled && (
        <button
          type="button"
          onClick={onSelectiveReview}
          disabled={isLoading || hasReport || isEditDisabled || selectedCount === 0}
          title={hasReport ? '보고서가 생성되어 수정할 수 없습니다' : undefined}
          className={`
            flex items-center gap-1 px-2 py-1.5 rounded-control text-xs font-medium whitespace-nowrap flex-shrink-0
            transition-colors disabled:opacity-50
            ${selectedCount > 0 && !hasReport
              ? 'bg-green-50 text-green-600 hover:bg-green-100'
              : 'bg-white text-ink-400 border'
            }
          `}
        >
          <CheckIcon className="h-3.5 w-3.5" />
          {selectedCount}
        </button>
        )}

        {/* 선택 삭제 버튼 */}
        <button
          type="button"
          onClick={onSelectiveDelete}
          disabled={isLoading || hasReport || isEditDisabled || selectedCount === 0}
          title={hasReport ? '보고서가 생성되어 수정할 수 없습니다' : undefined}
          className={`
            flex items-center gap-1 px-2 py-1.5 rounded-control text-xs font-medium whitespace-nowrap flex-shrink-0
            transition-colors disabled:opacity-50
            ${selectedCount > 0 && !hasReport
              ? 'bg-red-50 text-red-600 hover:bg-red-100'
              : 'bg-white text-ink-400 border'
            }
          `}
        >
          삭제 {selectedCount}
        </button>

        {/* 검토 진행률 표시 (findingSelectionEnabled 시 분석중만 표시) */}
        <div className="ml-auto flex items-center gap-1.5 text-xs whitespace-nowrap flex-shrink-0">
          {reviewProgress.analyzing !== undefined && reviewProgress.analyzing > 0 && (
            <span className="text-brand-600">
              <span className="inline-block w-1.5 h-1.5 bg-brand-500 rounded-full animate-pulse mr-0.5" />
              {reviewProgress.analyzing}
            </span>
          )}
          {!findingSelectionEnabled && reviewProgress.pendingReview !== undefined && reviewProgress.pendingReview > 0 && (
            <span className="text-amber-600">
              대기{reviewProgress.pendingReview}
            </span>
          )}
          {!findingSelectionEnabled && reviewProgress.reviewed > 0 && (
            <span className="text-green-600">
              완료{reviewProgress.reviewed}
            </span>
          )}
          {!findingSelectionEnabled && isAllReviewed && (reviewProgress.analyzing === undefined || reviewProgress.analyzing === 0) && (
            <CheckIcon className="h-4 w-4 text-green-600" />
          )}
        </div>
      </div>

      {/* 안내 문구 (findingSelectionEnabled 시)
          ⚠️ **상태를 반영한다** — 종전엔 선택 진행과 무관하게 「선택하세요」가 상시 떠 있어서,
          다 고른 뒤에도 그 문구만 보이고 실제로 막는 것(참여자 점검완료 서명 N/M)은 화면에
          없었다(2026-10-08 dev 실측). 사유 정본은 `lib/reportGate`이고 호출부(레이아웃)가
          `reportDisabledReason`으로 넘겨 주므로 버튼 `title`과 **같은 문자열**이다. */}
      {findingSelectionEnabled && !hasReport && (
        <div className="px-4 py-1.5 text-xs text-brand-600 font-medium border-b border-brand-100 bg-brand-50" role="status">
          {!canGenerateReport && reportDisabledReason
            ? reportDisabledReason
            : '보고서를 생성할 수 있습니다'}
        </div>
      )}

      {/* 하단 행: 촬영, 점검완료, 보고서 생성 */}
      <div className="flex items-center gap-2 px-4 py-2.5">
        {onOpenCamera && (
          <button
            type="button"
            onClick={onOpenCamera}
            disabled={isLoading || hasReport}
            className={`px-2 py-1.5 rounded-control text-xs whitespace-nowrap active:bg-ink-100 flex-shrink-0 ${hasReport ? 'text-ink-400 cursor-not-allowed' : 'text-ink-600'}`}
          >
            <svg className="w-4 h-4 inline-block mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            촬영
          </button>
        )}

        {/* 점검완료 현황
            ⚠️ 종전 게이트는 `totalCount > 1`이라 **기여자가 한 명이면 현황이 0개**였다 — 그때도
            서명이 보고서 생성을 막으므로 사용자는 막힌 이유를 숫자로 볼 자리가 없었다(Desktop
            힌트 슬롯과 같은 결함). 「아직 다 안 모였으면」 인원수와 무관하게 보인다. */}
        {completion?.enabled && completion.totalCount > 0
          && (completion.completedCount !== completion.totalCount || completion.totalCount > 1) && (
          <span className={`text-xs font-medium flex-shrink-0 ${completion.completedCount === completion.totalCount ? 'text-green-600' : 'text-ink-500'}`}>
            {completion.completedCount}/{completion.totalCount}
          </span>
        )}

        {/* 점검완료 서명 버튼 */}
        {completion?.enabled && completion.canSign && (
          <Button
            type="button"
            variant="success"
            className="whitespace-nowrap"
            onClick={completion.onSign}
            disabled={completion.isSubmitting}
            title={completion.signDisabledReason || undefined}
          >
            점검완료
          </Button>
        )}

        {/* 점검완료 취소/요청 버튼 */}
        {completion?.enabled && (completion.canCancel || completion.canRequest) && (
          <Button
            type="button"
            variant="secondary"
            className="whitespace-nowrap"
            onClick={completion.onManage}
            disabled={completion.isSubmitting}
          >
            {completion.totalCount <= 1 ? '취소' : '취소/요청'}
          </Button>
        )}

        {/* 보고서 생성 버튼 */}
        <Button
          type="button"
          variant="primary"
          className="ml-auto whitespace-nowrap"
          onClick={onGenerateReport}
          disabled={isLoading || !canGenerateReport}
          title={!canGenerateReport ? reportDisabledReason : undefined}
          // 터치 기기에서는 `title`을 띄울 방법이 없다 — 사유는 위 안내 배너가 보이고, 이
          // `aria-describedby`가 스크린리더 경로를 맡는다(사유 정본 `lib/reportGate`).
          aria-describedby={
            !canGenerateReport && reportDisabledReason ? REPORT_DISABLED_REASON_ID : undefined
          }
        >
          {reportLabel || '보고서 생성'}
        </Button>
        {!canGenerateReport && reportDisabledReason && (
          <span id={REPORT_DISABLED_REASON_ID} className="sr-only">
            {reportDisabledReason}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * 간단한 고정 버튼 (FAB 스타일)
 */
interface FloatingActionButtonProps {
  /** 클릭 핸들러 */
  onClick: () => void;
  /** 아이콘 컴포넌트(`components/icons`). 문자열 글리프는 받지 않는다. */
  icon: IconComponent;
  /** 라벨 (선택) */
  label?: string;
  /** 비활성화 */
  disabled?: boolean;
  /** 위치 */
  position?: 'bottom-right' | 'bottom-center';
}

export function FloatingActionButton({
  onClick,
  icon: Icon,
  label,
  disabled = false,
  position = 'bottom-right',
}: FloatingActionButtonProps) {
  const positionClasses = {
    'bottom-right': 'bottom-20 right-4',
    'bottom-center': 'bottom-20 left-1/2 -translate-x-1/2',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        fixed z-30 bg-brand-600 text-white rounded-full shadow-card
        flex items-center gap-2 transition-all
        active:scale-95 disabled:opacity-50
        ${positionClasses[position]}
        ${label ? 'px-5 py-3' : 'w-14 h-14 justify-center'}
      `}
    >
      <Icon className="h-6 w-6" />
      {label && <span className="font-medium">{label}</span>}
    </button>
  );
}

export default BottomActionBar;
